import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useHasRole } from "@/hooks/useHasRole";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { RefreshCw } from "lucide-react";

const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function generateCode(len = 8): string {
  let out = "";
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  for (let i = 0; i < len; i++) out += CODE_ALPHABET[bytes[i] % CODE_ALPHABET.length];
  return out;
}

const fmtBRL = (c?: number | null) => ((c ?? 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type Partner = any;
type Conversion = any;
type Payout = any;

export function ReferralsAdminContent() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [busy, setBusy] = useState(true);

  const reload = useCallback(async () => {
    setBusy(true);
    const [{ data: p }, { data: c }, { data: po }] = await Promise.all([
      supabase.from("referral_partners").select("*").order("created_at", { ascending: false }),
      supabase.from("referral_conversions").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.from("referral_payouts").select("*").order("created_at", { ascending: false }).limit(200),
    ]);
    setPartners(p || []); setConversions(c || []); setPayouts(po || []);
    setBusy(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const call = async (action: string, payload: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke("referral-partner-admin", { body: { action, ...payload } });
    if (error || (data as any)?.error) { toast.error((data as any)?.error || error?.message || "Erro"); return null; }
    if ((data as any)?.message) toast.info((data as any).message); else toast.success("Ação concluída");
    reload();
    return data;
  };

  if (busy) return <Skeleton className="h-64" />;

  return (
    <Tabs defaultValue="partners" className="space-y-4">
            <TabsList>
              <TabsTrigger value="partners">Parceiros ({partners.length})</TabsTrigger>
              <TabsTrigger value="conversions">Conversões</TabsTrigger>
              <TabsTrigger value="payouts">Pagamentos</TabsTrigger>
            </TabsList>

            <TabsContent value="partners" className="space-y-3">
              <div className="flex justify-end"><CreatePartnerDialog onCreated={reload} /></div>
              <Card><CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>E-mail</TableHead><TableHead>Código</TableHead><TableHead>Status</TableHead><TableHead>Comissão</TableHead><TableHead>Ações</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {partners.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.email}</TableCell>
                        <TableCell><code>{p.code}</code></TableCell>
                        <TableCell><Badge variant="outline">{p.status}</Badge></TableCell>
                        <TableCell>{p.commission_amount_cents ? fmtBRL(p.commission_amount_cents) : <span className="text-muted-foreground text-xs">padrão</span>}</TableCell>
                        <TableCell className="space-x-1">
                          <EditPartnerDialog partner={p} onSaved={reload} />
                          {p.status === "pending_approval" && (
                            <>
                              <Button size="sm" onClick={() => call("approve_partner", { partner_id: p.id })}>Aprovar</Button>
                              <Button size="sm" variant="outline" onClick={() => call("reject_partner", { partner_id: p.id })}>Rejeitar</Button>
                            </>
                          )}
                          {p.status === "active" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => call("create_payout", { partner_id: p.id })}>Gerar pagamento</Button>
                              <Button size="sm" variant="outline" onClick={() => call("suspend_partner", { partner_id: p.id })}>Suspender</Button>
                            </>
                          )}
                          {p.status === "suspended" && (
                            <Button size="sm" onClick={() => call("approve_partner", { partner_id: p.id })}>Reativar</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="conversions">
              <Card><CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Parceiro</TableHead><TableHead>Status</TableHead><TableHead>Comissão</TableHead><TableHead>Bruto</TableHead><TableHead>Ações</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {conversions.map(c => {
                      const partner = partners.find(p => p.id === c.partner_id);
                      return (
                        <TableRow key={c.id}>
                          <TableCell>{format(parseISO(c.created_at), "dd/MM/yyyy")}</TableCell>
                          <TableCell>{partner?.name || c.partner_id}</TableCell>
                          <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                          <TableCell>{fmtBRL(c.commission_amount_cents)}</TableCell>
                          <TableCell>{fmtBRL(c.gross_amount_cents)}</TableCell>
                          <TableCell className="space-x-1">
                            {c.status === "pending" && (
                              <>
                                <Button size="sm" onClick={() => call("mark_conversion_status", { conversion_id: c.id, status: "approved" })}>Aprovar</Button>
                                <Button size="sm" variant="outline" onClick={() => call("mark_conversion_status", { conversion_id: c.id, status: "rejected" })}>Rejeitar</Button>
                              </>
                            )}
                            {c.status === "approved" && (
                              <Button size="sm" variant="outline" onClick={() => call("mark_conversion_status", { conversion_id: c.id, status: "refunded" })}>Marcar estornada</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="payouts">
              <Card><CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Parceiro</TableHead><TableHead>Total</TableHead><TableHead>Conversões</TableHead><TableHead>Status</TableHead><TableHead>Ações</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {payouts.map(p => {
                      const partner = partners.find(pp => pp.id === p.partner_id);
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{format(parseISO(p.created_at), "dd/MM/yyyy")}</TableCell>
                          <TableCell>{partner?.name || p.partner_id}</TableCell>
                          <TableCell>{fmtBRL(p.total_amount_cents)}</TableCell>
                          <TableCell>{p.conversion_count}</TableCell>
                          <TableCell><Badge variant="outline">{p.status}</Badge></TableCell>
                          <TableCell className="space-x-1">
                            {p.status === "pending" && (
                              <>
                                <MarkPaidDialog payoutId={p.id} onDone={reload} />
                                <Button size="sm" variant="outline" onClick={() => call("cancel_payout", { payout_id: p.id })}>Cancelar</Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </TabsContent>
          </Tabs>
  );
}

export default function AdminReferralsPage() {
  const isAdmin = useHasRole("careos_admin");
  if (isAdmin === null) return <AppLayout title="Indicações"><Skeleton className="h-32" /></AppLayout>;
  if (!isAdmin) return <Navigate to="/app" replace />;
  return (
    <AppLayout title="Programa de indicações">
      <div className="p-4 space-y-4">
        <ReferralsAdminContent />
      </div>
    </AppLayout>
  );
}

function CreatePartnerDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", partner_type: "outro", code: "", commission_amount_cents: "" });
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    const payload: any = { action: "create_partner", ...form };
    if (form.commission_amount_cents) payload.commission_amount_cents = parseInt(form.commission_amount_cents, 10);
    const { data, error } = await supabase.functions.invoke("referral-partner-admin", { body: payload });
    if (error || (data as any)?.error) { toast.error((data as any)?.error || error?.message || "Erro"); return; }
    toast.success("Parceiro criado e ativo");
    setOpen(false); onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button>Novo parceiro</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo parceiro (já aprovado)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div><Label>E-mail *</Label><Input value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div><Label>Telefone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.partner_type} onValueChange={v => set("partner_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["farmacia","profissional_saude","cuidador","clinica","outro"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Código (opcional)</Label>
            <div className="flex gap-2">
              <Input value={form.code} onChange={e => set("code", e.target.value)} placeholder="Auto-gerado se vazio" />
              <Button type="button" variant="outline" size="icon" onClick={() => set("code", generateCode(8))} title="Gerar código">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div><Label>Comissão custom (centavos)</Label><Input type="number" value={form.commission_amount_cents} onChange={e => set("commission_amount_cents", e.target.value)} placeholder="ex: 5000 = R$50" /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Criar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({ payoutId, onDone }: { payoutId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const submit = async () => {
    const { data, error } = await supabase.functions.invoke("referral-partner-admin", {
      body: { action: "mark_payout_paid", payout_id: payoutId, payment_proof_url: url || null },
    });
    if (error || (data as any)?.error) { toast.error((data as any)?.error || error?.message || "Erro"); return; }
    toast.success("Marcado como pago"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm">Marcar pago</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirmar pagamento</DialogTitle></DialogHeader>
        <div><Label>URL do comprovante (opcional)</Label><Input value={url} onChange={e => setUrl(e.target.value)} /></div>
        <DialogFooter><Button onClick={submit}>Confirmar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPartnerDialog({ partner, onSaved }: { partner: any; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: partner.name || "",
    phone: partner.phone || "",
    partner_type: partner.partner_type || "outro",
    code: partner.code || "",
    document: partner.document || "",
    pix_key: partner.pix_key || "",
    commission_amount_cents: partner.commission_amount_cents != null ? String(partner.commission_amount_cents) : "",
    notes: partner.notes || "",
  });
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    const payload: any = {
      action: "update_partner",
      partner_id: partner.id,
      name: form.name,
      phone: form.phone || null,
      partner_type: form.partner_type,
      code: form.code,
      document: form.document || null,
      pix_key: form.pix_key || null,
      notes: form.notes || null,
      commission_amount_cents: form.commission_amount_cents ? parseInt(form.commission_amount_cents, 10) : null,
    };
    const { data, error } = await supabase.functions.invoke("referral-partner-admin", { body: payload });
    if (error || (data as any)?.error) { toast.error((data as any)?.error || error?.message || "Erro"); return; }
    toast.success("Parceiro atualizado"); setOpen(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline">Editar</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Editar parceiro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div><Label>Telefone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.partner_type} onValueChange={v => set("partner_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["farmacia","profissional_saude","cuidador","clinica","outro"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Código</Label>
            <div className="flex gap-2">
              <Input value={form.code} onChange={e => set("code", e.target.value)} />
              <Button type="button" variant="outline" size="icon" onClick={() => set("code", generateCode(8))} title="Gerar novo código">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div><Label>Documento</Label><Input value={form.document} onChange={e => set("document", e.target.value)} /></div>
          <div><Label>Chave PIX</Label><Input value={form.pix_key} onChange={e => set("pix_key", e.target.value)} /></div>
          <div><Label>Comissão custom (centavos)</Label><Input type="number" value={form.commission_amount_cents} onChange={e => set("commission_amount_cents", e.target.value)} placeholder="vazio = padrão" /></div>
          <div><Label>Notas</Label><Input value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
